<script lang="ts">
	/**
	 * The admin refund form (Design §9.6).
	 *
	 * Two things here are rules rather than layout:
	 *
	 * - **The revoke toggle's default follows the full/partial rule** — revoke on a full refund,
	 *   keep on a partial one — and it is *explicit* on the request, because §7.1 reads that flag to
	 *   decide whether a partially refunded order still contributes an entitlement. Once the admin
	 *   moves it, their choice stands: recomputing the default under them is how somebody ends up
	 *   revoking access they had deliberately chosen to keep.
	 * - **The note is mandatory** (§12). A refund with no stated reason is indistinguishable from a
	 *   mistake six months later, and the audit entry is the only record that survives.
	 *
	 * The amount is entered in the currency's own units and converted to minor units on submit: an
	 * admin typing "19.99" must not send nineteen minor units, and a form that takes minor units
	 * directly is a rounding error waiting for a hurried afternoon.
	 */

	import { createEventDispatcher } from 'svelte';
	import { Button, Input, Textarea, Toggle } from 'flowbite-svelte';
	import { FormField } from '../../../components';
	import { issueAdminRefund } from '../../../AppNetwork';
	import { currencyRow, formatAmount } from '../../../stores/storefront';
	import { toastError, toastSuccess } from '../../../stores/toast';
	import type { AdminInvoice, AdminSaleOrder } from '../../../AppTypes';

	/**
	 * What is being refunded — an invoice, or a **one-time purchase** that never had one (§4.2).
	 *
	 * Two props rather than a discriminated target, because every caller knows which of the two it
	 * is holding and the form's own rules are identical either way: the same settled amount, the
	 * same note, the same revoke default. What differs is one field on the request, and the server
	 * resolves an order that *does* have an invoice back to that invoice, so one charge can never
	 * be refunded twice under two names.
	 */
	export let invoice: AdminInvoice | null = null;
	export let order: AdminSaleOrder | null = null;

	const dispatch = createEventDispatcher<{ issued: { uuid: string }; cancel: void }>();

	/**
	 * What actually settled through the charge — never `total` on an invoice, which may include
	 * customer credit. An order has no such split: what it holds is what was charged.
	 */
	$: settled = invoice !== null ? invoice.amountPaid ?? invoice.total ?? 0 : order?.total ?? 0;
	$: currency = invoice?.currency ?? order?.currency ?? '';
	$: digits = $currencyRow(currency)?.minorUnitDigits ?? 2;
	$: divisor = Math.pow(10, digits);

	let amountText: string = '';
	/** One-shot: the prefill must not snap back while the admin is clearing the field to retype it. */
	let prefilled: boolean = false;
	let note: string = '';
	let revokeEntitlement: boolean = true;
	/** True once the admin has moved the toggle, after which the default stops applying. */
	let revokeTouched: boolean = false;
	let working: boolean = false;

	/** Prefilled with the whole refundable amount, which is what most refunds are. */
	$: if (!prefilled && settled > 0) {
		amountText = (settled / divisor).toFixed(digits);
		prefilled = true;
	}

	$: amountMinor = Math.round(Number(amountText.replace(',', '.')) * divisor);
	$: amountValid = Number.isFinite(amountMinor) && amountMinor > 0 && amountMinor <= settled;
	$: isFull = amountValid && amountMinor >= settled;

	// The §9.6 default, applied until the admin says otherwise.
	$: if (!revokeTouched) {
		revokeEntitlement = isFull;
	}

	async function fnSubmit(): Promise<void> {
		if (!amountValid || note.trim() === '') return;

		working = true;
		try {
			const refund = await issueAdminRefund({
				...(invoice !== null ? { invoiceUUID: invoice.uuid } : { orderUUID: order?.uuid }),
				// Explicit even when it is the whole amount: the server re-derives what is refundable
				// and refuses anything above it, and a figure on the request is what the audit records.
				amount: amountMinor,
				note: note.trim(),
				revokeEntitlement
			});
			toastSuccess(`Refunded ${$formatAmount(refund.amount, refund.currency) ?? ''}.`);
			dispatch('issued', { uuid: refund.uuid });
		} catch (error) {
			toastError((error as Error).message);
		} finally {
			working = false;
		}
	}
</script>

<form class="space-y-4" on:submit|preventDefault={fnSubmit} data-testid="admin-refund-form">
	<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
		<FormField
			label="Amount"
			description={`Up to ${$formatAmount(settled, currency) ?? settled} settled through the charge.`}
			error={amountText !== '' && !amountValid ? 'Enter an amount between zero and what was charged.' : ''}
		>
			<Input bind:value={amountText} placeholder="0.00" />
		</FormField>

		<FormField
			label="Entitlement"
			description={isFull
				? 'A full refund revokes access by default.'
				: 'A partial refund keeps access by default.'}
		>
			<Toggle
				bind:checked={revokeEntitlement}
				on:change={() => (revokeTouched = true)}
			>
				{revokeEntitlement ? 'Revoke access' : 'Keep access'}
			</Toggle>
		</FormField>
	</div>

	<FormField label="Note" required description="Recorded in the audit trail with your name.">
		<Textarea rows={2} bind:value={note} placeholder="Why is this being refunded?" />
	</FormField>

	<div class="flex gap-2">
		<Button type="submit" color="red" size="sm" disabled={working || !amountValid || note.trim() === ''}>
			{working ? 'Refunding…' : 'Issue refund'}
		</Button>
		<Button color="alternative" size="sm" on:click={() => dispatch('cancel')}>Cancel</Button>
	</div>
</form>
